import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TechnologyIntegration = () => {
  const [selectedCategory, setSelectedCategory] = useState('cloud');

  const integrationCategories = [
    {
      id: 'cloud',
      name: 'Cloud Platforms',
      icon: 'Cloud',
      description: 'Native integration with major cloud providers'
    },
    {
      id: 'devops',
      name: 'DevOps Tools',
      icon: 'GitBranch',
      description: 'CI/CD and deployment automation'
    },
    {
      id: 'monitoring',
      name: 'Monitoring',
      icon: 'Activity',
      description: 'Observability and performance tracking'
    },
    {
      id: 'databases',
      name: 'Databases',
      icon: 'Database',
      description: 'Data storage and management solutions'
    }
  ];

  const technologyStacks = {
    cloud: [
      { name: 'AWS', logo: 'https://images.unsplash.com/photo-1523474253046-8cd2748b5fd2?w=100&h=100&fit=crop', compatibility: 'Native', services: ['EC2', 'Lambda', 'RDS', 'S3'] },
      { name: 'Google Cloud', logo: 'https://images.unsplash.com/photo-1573804633927-bfcbcd909acd?w=100&h=100&fit=crop', compatibility: 'Native', services: ['Compute Engine', 'Cloud Functions', 'BigQuery'] },
      { name: 'Microsoft Azure', logo: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=100&h=100&fit=crop', compatibility: 'Native', services: ['App Service', 'Functions', 'Cosmos DB'] },
      { name: 'DigitalOcean', logo: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=100&h=100&fit=crop', compatibility: 'Supported', services: ['Droplets', 'Kubernetes', 'Spaces'] }
    ],
    devops: [
      { name: 'Jenkins', logo: 'https://images.unsplash.com/photo-1618401471353-b98afee0b2eb?w=100&h=100&fit=crop', compatibility: 'Native', services: ['Pipeline', 'Build', 'Deploy'] },
      { name: 'GitLab CI/CD', logo: 'https://images.unsplash.com/photo-1556075798-4825dfaaf498?w=100&h=100&fit=crop', compatibility: 'Native', services: ['Pipelines', 'Registry', 'Security'] },
      { name: 'GitHub Actions', logo: 'https://images.unsplash.com/photo-1618401471353-b98afee0b2eb?w=100&h=100&fit=crop', compatibility: 'Native', services: ['Workflows', 'Packages', 'Security'] },
      { name: 'Docker', logo: 'https://images.unsplash.com/photo-1605745341112-85968b19335a?w=100&h=100&fit=crop', compatibility: 'Native', services: ['Containers', 'Compose', 'Swarm'] }
    ],
    monitoring: [
      { name: 'Datadog', logo: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=100&h=100&fit=crop', compatibility: 'Native', services: ['APM', 'Logs', 'Infrastructure'] },
      { name: 'New Relic', logo: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=100&h=100&fit=crop', compatibility: 'Native', services: ['APM', 'Browser', 'Mobile'] },
      { name: 'Prometheus', logo: 'https://images.unsplash.com/photo-1518186285589-2f7649de83e0?w=100&h=100&fit=crop', compatibility: 'Native', services: ['Metrics', 'Alerting', 'Grafana'] },
      { name: 'Splunk', logo: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=100&h=100&fit=crop', compatibility: 'Supported', services: ['Logs', 'Analytics', 'Security'] }
    ],
    databases: [
      { name: 'PostgreSQL', logo: 'https://images.unsplash.com/photo-1544383835-bda2bc66a55d?w=100&h=100&fit=crop', compatibility: 'Native', services: ['ACID', 'JSON', 'Extensions'] },
      { name: 'MongoDB', logo: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=100&h=100&fit=crop', compatibility: 'Native', services: ['Document', 'Atlas', 'Compass'] },
      { name: 'Redis', logo: 'https://images.unsplash.com/photo-1518186285589-2f7649de83e0?w=100&h=100&fit=crop', compatibility: 'Native', services: ['Cache', 'Pub/Sub', 'Streams'] },
      { name: 'Elasticsearch', logo: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=100&h=100&fit=crop', compatibility: 'Supported', services: ['Search', 'Analytics', 'Kibana'] }
    ]
  };

  const selectedTechnologies = technologyStacks[selectedCategory] || [];

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
          <Icon name="Puzzle" size={20} className="text-accent" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-text-primary">Technology Integration</h3>
          <p className="text-sm text-text-secondary">Compatible with your existing technology stack</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {integrationCategories.map((category) => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-all ${
              selectedCategory === category.id
                ? 'border-accent bg-accent/10 text-accent' :'border-border hover:border-accent/50 text-text-secondary hover:text-text-primary'
            }`}
          >
            <Icon name={category.icon} size={16} />
            <span className="text-sm font-medium">{category.name}</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {selectedTechnologies.map((tech, index) => (
          <div key={index} className="border border-border rounded-lg p-4 hover:border-accent/50 transition-colors">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 rounded-lg overflow-hidden bg-muted">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = '/assets/images/no_image.png';
                  }}
                />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-text-primary">{tech.name}</h4>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    tech.compatibility === 'Native' ?'bg-success/10 text-success' :'bg-warning/10 text-warning'
                  }`}>
                    {tech.compatibility}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-1">
              {tech.services.map((service, serviceIndex) => (
                <span
                  key={serviceIndex}
                  className="px-2 py-1 bg-muted text-text-secondary text-xs rounded"
                >
                  {service}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-muted rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} className="text-accent flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-text-primary mb-1">Custom Integrations</h4>
            <p className="text-sm text-text-secondary">
              Don't see your technology stack? We specialize in custom integrations and can work with any modern technology. 
              Contact us to discuss your specific requirements.
            </p>
            <Button
              variant="outline"
              size="sm"
              iconName="MessageCircle"
              iconPosition="left"
              className="mt-3"
            >
              Discuss Custom Integration
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnologyIntegration;